// ===============================================================================
// ResetPassword.js - Password Reset with Token Page
// ===============================================================================

import React, { useState, useEffect } from "react";
import { authAPI, validatePassword } from "./api";
import "./Login.css";

function ResetPassword() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [showPasswordHints, setShowPasswordHints] = useState(false);

  // Get token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      setToken(urlToken);
    }
  }, []);

  // Validate password on change
  useEffect(() => {
    if (password) {
      setPasswordStrength(validatePassword(password));
    }
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    // Validate password
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError("Password doesn't meet requirements: " + validation.errors.join(", "));
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      console.error("Reset password error:", err);
      const errorDetail = err?.response?.data?.detail;
      setError(errorDetail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="logo">
            <span className="logo-icon">💰</span>
            <h1>SplitMon</h1>
          </div>

          <div className="email-sent-icon">✅</div>
          
          <h2>Password Reset Successful!</h2>
          
          <p className="check-email-text">
            Your password has been updated successfully.
            <br /><br />
            You can now log in with your new password.
          </p>

          <form>
            <a href="/login" style={{ textDecoration: 'none' }}>
              <button type="button" style={{ width: '100%' }}>
                Go to Login
              </button>
            </a>
          </form>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="logo">
            <span className="logo-icon">💰</span>
            <h1>SplitMon</h1>
          </div>

          <div className="message error">
            Invalid reset link. Please request a new password reset.
          </div>

          <div className="back-link">
            <a href="/forgot-password">Request New Reset Link</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="logo">
          <span className="logo-icon">💰</span>
          <h1>SplitMon</h1>
        </div>

        <h2>Reset Your Password</h2>
        <p className="subtitle">
          Enter your new password below.
        </p>

        {error && <div className="message error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="password-input-wrapper">
            <input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setShowPasswordHints(true)}
              onBlur={() => setShowPasswordHints(false)}
              disabled={loading}
              required
              autoComplete="new-password"
            />
            
            {/* Password Strength Indicator */}
            {password && (
              <div className="password-strength">
                <div className={`strength-bar ${passwordStrength.isValid ? 'strong' : password.length > 4 ? 'medium' : 'weak'}`}>
                  <div className="strength-fill"></div>
                </div>
                <span className="strength-text">
                  {passwordStrength.isValid ? '✓ Strong' : password.length > 4 ? 'Medium' : 'Weak'}
                </span>
              </div>
            )}
            
            {/* Password Requirements */}
            {showPasswordHints && !passwordStrength.isValid && (
              <div className="password-hints">
                <p>Password must have:</p>
                <ul>
                  <li className={password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[A-Z]/.test(password) ? 'met' : ''}>One uppercase letter</li>
                  <li className={/[a-z]/.test(password) ? 'met' : ''}>One lowercase letter</li>
                  <li className={/\d/.test(password) ? 'met' : ''}>One number</li>
                  <li className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? 'met' : ''}>One special character</li>
                </ul>
              </div>
            )}
          </div>

          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            required
            autoComplete="new-password"
          />

          <button type="submit" disabled={loading || !passwordStrength.isValid}>
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div className="back-link">
          <a href="/login">← Back to Login</a>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;

